// LaserCutClaySculpture.js — Interlocking-style slicing + slotting, Clay UI kept
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { sliceModelXZ, sliceModelYZ } from "../utils/sliceModel";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import TutorialWindow from "../components/TutorialWindow";
import { InterlockingClayWrap } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import "../styles/common.css";
import { downloadSVG } from "../utils/exportUtils";
import { MACHINE_PRESETS } from "../components/Sidebar";
import { Brush, Evaluator, ADDITION, SUBTRACTION } from "three-bvh-csg";
import { handleSeparateExport } from "../utils/separateSVGExport";

const getSelectedLaserPreset = () => {
  const selected =
    window.labConstraints?.machineSettings?.laserCutter?.selected ||
    "Trotec Speedy 400";
  const bed =
    MACHINE_PRESETS.laserCutter[selected] ||
    MACHINE_PRESETS.laserCutter["Trotec Speedy 400"];
  return { selected, bed };
};
// ===== 计算交线 & 槽位（从 Interlocking 复用）=====
const calculateIntersections = (
  horizontalContours,
  verticalContours,
  materialThickness
) => {
  const result = { lines: [], slots: { horizontal: [], vertical: [] } };
  horizontalContours.forEach((hContour, hIndex) => {
    verticalContours.forEach((vContour, vIndex) => {
      const hY = hContour.y;
      const vX = vContour.x;
      const verticalPoints = vContour.points;
      const yCoords = verticalPoints.map((p) => p.x);
      const minY = Math.min(...yCoords);
      const maxY = Math.max(...yCoords);
      if (minY <= hY && hY <= maxY) {
        const projectedPoints = [];
        for (let i = 0; i < verticalPoints.length; i++) {
          const curr = verticalPoints[i];
          const next = verticalPoints[(i + 1) % verticalPoints.length];
          if ((curr.x - hY) * (next.x - hY) > 0) continue;
          const t = (hY - curr.x) / (next.x - curr.x);
          const z = curr.y + t * (next.y - curr.y);
          projectedPoints.push(new THREE.Vector2(vX, z));
        }
        if (projectedPoints.length >= 2) {
          projectedPoints.sort((a, b) => a.y - b.y);
          const firstPoint = projectedPoints[0];
          const lastPoint = projectedPoints.at(-1);
          const direction = new THREE.Vector2()
            .subVectors(lastPoint, firstPoint)
            .normalize();
          const offset = direction
            .clone()
            .multiplyScalar(materialThickness * 0.5);
          const start = new THREE.Vector2().copy(firstPoint).sub(offset);
          const end = new THREE.Vector2().copy(lastPoint).add(offset);

          const lineStart = new THREE.Vector3(vX, hY, start.y);
          const lineEnd = new THREE.Vector3(vX, hY, end.y);
          result.lines.push({ start: lineStart, end: lineEnd, hIndex, vIndex });

          const mid = new THREE.Vector3()
            .addVectors(lineStart, lineEnd)
            .multiplyScalar(0.5);
          // 注意：这里按照 Interlocking 的定义：水平片的槽朝 Z，垂直片的槽朝 Y
          result.slots.horizontal.push({
            center: new THREE.Vector3()
              .addVectors(lineStart, mid)
              .multiplyScalar(0.5),
            length: lineStart.distanceTo(mid),
            width: materialThickness * 1.1,
            height: materialThickness * 1.1,
            direction: new THREE.Vector3(0, 0, 1),
            hIndex,
            vIndex,
          });
          result.slots.vertical.push({
            center: new THREE.Vector3()
              .addVectors(mid, lineEnd)
              .multiplyScalar(0.5),
            length: mid.distanceTo(lineEnd),
            width: materialThickness * 1.1,
            height: materialThickness * 1.1,
            direction: new THREE.Vector3(0, 1, 0),
            hIndex,
            vIndex,
          });
        }
      }
    });
  });
  return result;
};

const applySlotsToParts = (horizontalPlanes, verticalPlanes, slots) => {
  const evaluator = new Evaluator();

  const apply = (planes, slotList, isVertical) => {
    planes.forEach((plane, index) => {
      const planeSlots = slotList.filter(
        (s) => (isVertical ? s.vIndex : s.hIndex) === index
      );
      if (!planeSlots.length) return;

      const planeBrush = new Brush(plane.geometry);
      planeBrush.position.copy(plane.position);
      planeBrush.rotation.copy(plane.rotation);
      planeBrush.scale.copy(plane.scale);
      planeBrush.updateMatrixWorld(true);

      const slotBrushes = planeSlots.map((slot) => {
        const box = new THREE.BoxGeometry(
          isVertical ? slot.width : slot.width,
          isVertical ? slot.length : slot.height,
          isVertical ? slot.height : slot.length
        );
        const b = new Brush(box);
        b.position.copy(slot.center);
        b.lookAt(new THREE.Vector3().addVectors(slot.center, slot.direction));
        b.updateMatrixWorld(true);
        return b;
      });

      let combined = slotBrushes[0];
      for (let i = 1; i < slotBrushes.length; i++) {
        combined = evaluator.evaluate(combined, slotBrushes[i], ADDITION);
      }

      const resultEval = evaluator.evaluate(planeBrush, combined, SUBTRACTION);
      const inv = new THREE.Matrix4().copy(plane.matrixWorld).invert();
      resultEval.geometry.applyMatrix4(inv);

      plane.geometry.dispose();
      plane.geometry = resultEval.geometry;
    });
  };

  apply(horizontalPlanes, slots.horizontal, false);
  apply(verticalPlanes, slots.vertical, true);
};

// ===== 主组件 =====
const LaserCutClaySculpture = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);

  const modelRef = useRef(null);
  const layersGroupRef = useRef(null);
  const shellsGroupRef = useRef(null);

  const verticalPlanesRef = useRef([]);
  const horizontalPlanesRef = useRef([]);
  const verticalContoursRef = useRef([]);
  const horizontalContoursRef = useRef([]);

  const { createVisualization } = useVisualHelpers();
  const pageId = useMemo(() => `laser-cut`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [cutNumber, setCutNumber] = useState(6);
  // const [materialThickness, setMaterialThickness] = useState(3);
  const [svgReady, setSvgReady] = useState(false);
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [constraintWarnings, setConstraintWarnings] = useState([]);
  const [laserCutterSettings] = useState({
    selected: "Glowforge Basic",
    expanded: false,
  });
  // ⬇️ put these near the top of the component body, before any usage
  const getBaseThickness = () => {
    const b = window.labConstraints?.materialBaseThickness ?? 3;
    // clamp to [1, 12]
    return Math.min(12, Math.max(1, Number(b) || 3));
  };

  // define the state FIRST (nothing above should reference materialThickness)
  const [materialThickness, setMaterialThickness] = React.useState(() => getBaseThickness());

  // derived slider props (these can come immediately after)
  const baseT = getBaseThickness();          // current base thickness from Sidebar
  const thicknessMin = baseT;                // slider min
  const thicknessMax = 12;                   // slider max
  const thicknessStep = baseT;               // snap step = base

  // react to Sidebar updates and re-snap the value
  React.useEffect(() => {
    const onLabUpdated = () => {
      const b = getBaseThickness();
      setMaterialThickness(prev => {
        const snapped = Math.round((Number(prev) || b) / b) * b;
        return Math.min(12, Math.max(b, snapped));
      });
    };
    window.addEventListener("labConstraints:updated", onLabUpdated);
    return () => window.removeEventListener("labConstraints:updated", onLabUpdated);
  }, []);

  const fastIterNotRecommended = useMemo(
    () => cutNumber > 6 || materialThickness >= 4,
    [cutNumber, materialThickness]
  );
  // Clay 壳材质（原样）
  const clayMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.95,
        metalness: 0.05,
        transparent: true,
        opacity: 0.55,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
        depthWrite: false,
      }),
    []
  );

  const createClayShell = useCallback(
    (mesh) => {
      const shell = mesh.clone();
      shell.material = clayMaterial;
      shell.scale.multiplyScalar(1.01);
      shell.renderOrder = 1;
      return shell;
    },
    [clayMaterial]
  );

  // 和 Interlocking 同款的闭合曲线构造
  const buildClosedCurveWorld = (segments, uniqueVertices, matrixWorld) => {
    const worldVertices = uniqueVertices.map((v) =>
      v.clone().applyMatrix4(matrixWorld)
    );
    const used = new Set();
    const curve = [];
    let current = segments[0].i1;
    curve.push(worldVertices[current]);
    let found = true;
    let safety = 0;
    while (found && safety < segments.length * 2) {
      found = false;
      for (let i = 0; i < segments.length; i++) {
        if (used.has(i)) continue;
        const s = segments[i];
        if (s.i1 === current) {
          current = s.i2;
          curve.push(worldVertices[current]);
          used.add(i);
          found = true;
          break;
        } else if (s.i2 === current) {
          current = s.i1;
          curve.push(worldVertices[current]);
          used.add(i);
          found = true;
          break;
        }
      }
      safety++;
    }
    if (!curve[0].equals(curve[curve.length - 1])) curve.push(curve[0].clone());
    return curve;
  };

  // 生成切片 + 槽
  const clearGroups = () => {
    const dispose = (g) => {
      if (!g) return;
      g.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose?.();
      });
      sceneRef.current.remove(g);
    };
    dispose(layersGroupRef.current);
    dispose(shellsGroupRef.current);
    layersGroupRef.current = new THREE.Group();
    shellsGroupRef.current = new THREE.Group();
    sceneRef.current.add(layersGroupRef.current);
    //sceneRef.current.add(shellsGroupRef.current);

    verticalPlanesRef.current = [];
    horizontalPlanesRef.current = [];
    verticalContoursRef.current = [];
    horizontalContoursRef.current = [];
  };

  const generateSlicesAndSlots = useCallback(() => {
    if (!modelRef.current || !sceneRef.current) return;
    clearGroups();

    const model = modelRef.current;
    model.updateMatrixWorld(true);
    const bbox = new THREE.Box3().setFromObject(model);

    const verticalX = Array.from(
      { length: cutNumber + 2 },
      (_, i) => bbox.min.x + (i / (cutNumber + 1)) * (bbox.max.x - bbox.min.x)
    );
    const horizontalY = Array.from(
      { length: cutNumber + 2 },
      (_, i) => bbox.min.y + (i / (cutNumber + 1)) * (bbox.max.y - bbox.min.y)
    );

    // 垂直片（YZ）
    verticalX.forEach((x) => {
      const res = sliceModelYZ(model, null, x);
      if (!res?.componentSegments) return;
      res.componentSegments.forEach((segs) => {
        if (segs.length < 2) return;
        const pts = buildClosedCurveWorld(
          segs,
          res.uniqueVertices,
          model.matrixWorld
        );
        if (pts.length < 3) return;
        const shape = new THREE.Shape(
          pts.map((p) => new THREE.Vector2(p.y, p.z))
        );
        const geom = new THREE.ExtrudeGeometry(shape, {
          depth: materialThickness,
          bevelEnabled: false,
        });
        const mesh = new THREE.Mesh(
          geom,
          new THREE.MeshStandardMaterial({
            color: 0xdcc7a1,
            side: THREE.DoubleSide,
          })
        );
        mesh.rotation.y = Math.PI / 2;
        mesh.rotation.x = Math.PI / 2;
        mesh.position.x = x - materialThickness / 2;
        mesh.updateMatrixWorld(true);
        layersGroupRef.current.add(mesh);
        verticalPlanesRef.current.push(mesh);
        verticalContoursRef.current.push({ x, points: shape.getPoints() });
      });
    });

    // 水平片（XZ）
    horizontalY.forEach((y) => {
      const res = sliceModelXZ(model, null, y);
      if (!res?.componentSegments) return;
      res.componentSegments.forEach((segs) => {
        if (segs.length < 2) return;
        const pts = buildClosedCurveWorld(
          segs,
          res.uniqueVertices,
          model.matrixWorld
        );
        if (pts.length < 3) return;
        const shape = new THREE.Shape(
          pts.map((p) => new THREE.Vector2(p.x, p.z))
        );
        const geom = new THREE.ExtrudeGeometry(shape, {
          depth: materialThickness,
          bevelEnabled: false,
        });
        const mesh = new THREE.Mesh(
          geom,
          new THREE.MeshStandardMaterial({
            color: 0xdcc7a1,
            side: THREE.DoubleSide,
          })
        );
        mesh.rotation.x = Math.PI / 2;
        mesh.position.y = y + materialThickness / 2;
        mesh.updateMatrixWorld(true);
        layersGroupRef.current.add(mesh);
        horizontalPlanesRef.current.push(mesh);
        horizontalContoursRef.current.push({ y, points: shape.getPoints() });
      });
    });

    // 计算槽并减掉
    const { slots } = calculateIntersections(
      horizontalContoursRef.current,
      verticalContoursRef.current,
      materialThickness
    );
    applySlotsToParts(
      horizontalPlanesRef.current,
      verticalPlanesRef.current,
      slots
    );

    // 槽应用完再加 Clay 外壳
    [...horizontalPlanesRef.current, ...verticalPlanesRef.current].forEach(
      (m) => {
        shellsGroupRef.current.add(createClayShell(m));
      }
    );

    setSvgReady(true);
    setTimeout(() => checkModelBBoxConstraints(), 0);
  }, [cutNumber, materialThickness, createClayShell]);

  // 一键整体预警：模型最长边 vs 床面最短边
// 在 LaserCutClaySculpture.js 中找到并修改：

const checkModelBBoxConstraints = useCallback(() => {
  if (!modelRef.current) return true;

  // Get selected laser cutter bed size
  const { selected, bed } = getSelectedLaserPreset();

  // Compute model bounding box size
  const bbox = new THREE.Box3().setFromObject(modelRef.current);
  const size = bbox.getSize(new THREE.Vector3()); // { x, y, z }

  const warnings = [];

  if (size.x > bed.width || size.y > bed.height) {
    warnings.push({
      type: "size_exceeded",
      part: "Imported Model",
      printer: selected,
      currentSize: {
        width: size.x.toFixed(1),
        height: size.y.toFixed(1),
      },
      maxSize: { width: bed.width, height: bed.height },
      message: `Model size ${size.x.toFixed(1)} × ${size.y.toFixed(1)} mm exceeds bed ${bed.width} × ${bed.height} mm`,
       
    });
  }

  setConstraintWarnings(warnings);
  return warnings.length === 0; 
}, []); // we pull live data from window.labConstraints each call

  // 导出：与 Interlocking 一样把两组轮廓拼一起
  const handleDownloadSVG = useCallback(() => {
    const combined = [
      ...horizontalContoursRef.current.map((c) => ({
        ...c,
        plane: "horizontal",
      })),
      ...verticalContoursRef.current.map((c) => ({ ...c, plane: "vertical" })),
    ];
    if (!combined.length) return;
    downloadSVG(combined);
  }, []);

  // ===== 场景 & 模型 =====
  const initScene = useCallback(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );
    const grid = new THREE.GridHelper(200, 20, 0x888888, 0xaaaaaa);
    grid.position.y = -0.1;
    grid.material.opacity = 0.3;
    grid.material.transparent = true;
    sceneRef.current.add(grid);
    sceneRef.current.add(new THREE.AxesHelper(100));
    return cleanup;
  }, []);

  const loadModel = useCallback(() => {
    const loader = new STLLoader();
    const path = stlFile || "/models/twistT.stl";
    loader.load(
      path,
      (geometry) => {
        geometry.computeBoundingBox();
        const c = geometry.boundingBox.getCenter(new THREE.Vector3());
        geometry.translate(-c.x, -c.y, -c.z);
        geometry.rotateX(-Math.PI / 2);
        geometry.rotateY(-Math.PI / 2);

        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshStandardMaterial({
            color: 0xffffff,
            opacity: 0.5,
            transparent: true,
          })
        );
        mesh.updateMatrixWorld(true);
        sceneRef.current.add(mesh);

        const bbox = new THREE.Box3().setFromObject(mesh);
        mesh.position.y -= bbox.min.y;
        mesh.updateMatrixWorld(true);

        if (modelRef.current && sceneRef.current)
          sceneRef.current.remove(modelRef.current);
        modelRef.current = mesh;

        // Clay 视效由 createVisualization 包裹
        // createVisualization(mesh, sceneRef.current, bbox);

        // 初次检查 + 生成切片/槽
        checkModelBBoxConstraints();
        setTimeout(() => generateSlicesAndSlots(), 30);
      },
      undefined,
      (e) => console.error("Error loading model", e)
    );
  }, [
    stlFile,
    createVisualization,
    checkModelBBoxConstraints,
    generateSlicesAndSlots,
  ]);

  useEffect(() => {
    const cleanup = initScene();
    loadModel();
    return cleanup;
  }, [initScene, loadModel]);

  // 参数变化时重建
  useEffect(() => {
    if (!modelRef.current) return;
    const t = setTimeout(() => generateSlicesAndSlots(), 150);
    return () => clearTimeout(t);
  }, [cutNumber, materialThickness, generateSlicesAndSlots]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container" />

      {/* 预警气泡（和 Interlocking 的样式一致） */}
      {constraintWarnings.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {constraintWarnings.map((w, i) => (
            <div
              key={i}
              style={{
                backgroundColor: "#ff9800",
                color: "#fff",
                padding: "12px 16px",
                borderRadius: 6,
                boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <span style={{ fontSize: 16, marginRight: 8 }}>⚠️</span>
                <strong>
                  {w.plane === "horizontal" ? "Horizontal" : "Vertical"} Layer{" "}
                  {w.layer} Exceeds {w.cutter}
                </strong>
              </div>
              <div style={{ fontSize: 13 }}>
                <div>
                  Current: {w.currentSize.width} × {w.currentSize.height} mm
                </div>
                <div>
                  Max: {w.maxSize.width} × {w.maxSize.height} mm
                </div>
              </div>
              <button
                onClick={() =>
                  setConstraintWarnings((prev) =>
                    prev.filter((_, idx) => idx !== i)
                  )
                }
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: "none",
                  border: "none",
                  color: "#fff",
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible((p) => !p)}
        tutorialContent={InterlockingClayWrap}
      />

      <button
        className="panel-toggle-button"
        onClick={() => setShowControlPanel(!showControlPanel)}
      >
        {showControlPanel ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {showControlPanel && (
        <div className="controls-panel">
          <div className="panel-title-row">
            <div className="panel-title">Laser Cut Clay Sculpture</div>
            {fastIterNotRecommended && (
              <span
                className="iteration-badge badge--warn"
                title="High manual work, not ideal for quick iteration"
              >
                Not ideal for quick iteration
              </span>
            )}
          </div>
          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
              <p>
                This workflow helps you quickly build an internal armature for
                clay sculpting. It converts your 3D model into interlocking
                laser-cut ribs with slots, forming a stable substructure that
                supports precise clay modeling on the exterior.
              </p>
              <p>
                You can control slice count, spacing, and material thickness to
                match your stock, then assemble the rib-and-slot frame and apply
                clay for final shaping and detail.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Recommended for flat sheet materials (e.g., plywood, MDF,
                cardboard, acrylic). The laser-cut frame is not the final
                surface—it serves as a quick, accurate guide for clay
                application.
              </p>
            </div>
          </div>

          <div className="panel-section">
            <div
              className="section-header"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <h4>Time & Effort</h4>
              {fastIterNotRecommended && (
                <span
                  style={{
                    fontSize: 12,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "#ff9800",
                    color: "#fff",
                    whiteSpace: "nowrap",
                  }}
                  title="High manual work, not ideal for quick iteration"
                >
                  Not Ideal for Quick Iteration
                </span>
              )}
            </div>
            <div className="section-content">
              <ul style={{ marginLeft: 16, lineHeight: 1.5 }}>
                <li>
                  Assembly involves <strong>significant manual work</strong>{" "}
                  (cutting, aligning, slot fitting, and post-processing). Total
                  hands-on time is <strong>hard to predict</strong> and scales
                  with the number of slices.
                </li>
                <li>
                  If your goal is <strong>fast iteration</strong>, this workflow
                  is <strong>not recommended</strong>. Consider low-res FDM
                  prints or coarse foam/stacked layers for early tests.
                </li>
                <li>
                  Want a <strong>rough laser-cut time</strong>? Check the{" "}
                  <strong>Laser Cut Interlocking 2</strong> workflow — it
                  estimates by total contour length. This page focuses on
                  geometry and slotting, so it{" "}
                  <strong>doesn’t compute cut time</strong>.
                </li>
              </ul>
              <p style={{ fontSize: 12, opacity: 0.85, marginTop: 8 }}>
                Tip: Start with fewer slices (e.g., 4–5) and thinner stock to
                validate fit and scale, then increase resolution for the final.
              </p>
            </div>
          </div>

          <div className="input-group">
            <label>Number of Slices</label>
            <div className="value-display">
              <input
                type="range"
                className="slider"
                min="3"
                max="10"
                value={cutNumber}
                onChange={(e) => setCutNumber(parseInt(e.target.value))}
              />
              <div className="value">{cutNumber}</div>
            </div>
          </div>

<div className="input-group">
            <label>Material Thickness (mm)</label>
              <div className="value-display">
                <input
                  type="range"
                  min={thicknessMin}
                  max={thicknessMax}
                  step={thicknessStep}
                  value={materialThickness}
                  onChange={(e) => setMaterialThickness(Number(e.target.value))}
                />
                <div className="value">{materialThickness} mm</div>
              </div>
          </div>

          <div className="buttons-container">
{/*          <button
  onClick={() => {
    // 使用与 LaserCutInterlocking 相同的导出函数
    handleSeparateExport({
      horizontalContours: horizontalContoursRef.current,
      verticalContours: verticalContoursRef.current,
      xpositive: [], // Clay 版本没有这些数据，传空数组
      xneg: [],
      lineGroups: [],
      materialThickness: materialThickness,
    });
  }}
  className="action-button"
  disabled={!svgReady}
>
  DOWNLOAD SVG FILES
</button> */}
            <button
              onClick={() => setTutorialVisible((p) => !p)}
              className="action-button"
            >
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaserCutClaySculpture;
